import * as yup from 'yup';
import isNil from 'lodash/isNil';
import pick from 'lodash/pick';
import queryString from 'query-string';
import { FormikHelpers, useFormikContext } from 'formik';
import { useDeepCompareEffect } from 'react-use';
import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';

import tracking, { setPageName, setPageRendered, setPageStart } from '../../utils/tracking';
import {
  DetailsStepContext,
  MixFormField,
  MixStepContext,
  QuoteBuilderStep,
  QuoteBuilderValues,
  quoteSignUpSchema,
  quoteSignUpSchema2,
  quoteStepValueKeys,
  quoteVerificationSchema,
} from './utils/formState';
import { OptionGroupName } from '../../store/catalogues/selectors';
import { PrivateRoutePath, PublicRoutePath } from '../../utils/routes';
import { TrackingEvent } from '../../utils/tracking/types';
import { currentCartOptionGroupsByProductTypes$ } from '../../store/orders/selectors';
import { endQuoteTracePage, setCurrentPageLeaver, startQuoteTracePage } from './utils/quoteTracking';
import { getOptions, getSchema, mergeSchemas, serviceAreaSchema } from '../../utils/orderForms';

export const stepOrder = [
  QuoteBuilderStep.DETAILS,
  QuoteBuilderStep.MIX,
  QuoteBuilderStep.REGISTER,
  QuoteBuilderStep.MOBILE,
];

export const trackingPageNames = {
  [QuoteBuilderStep.DETAILS]: 'region',
  [QuoteBuilderStep.MIX]: 'mix',
  [QuoteBuilderStep.REGISTER]: 'register',
  [QuoteBuilderStep.MOBILE]: 'passcode',
};

export const trackingPageEventNames = {
  [QuoteBuilderStep.DETAILS]: TrackingEvent.QUOTE_PAGE_REGION,
  [QuoteBuilderStep.MIX]: TrackingEvent.QUOTE_PAGE_MIX,
  [QuoteBuilderStep.REGISTER]: TrackingEvent.REGISTER,
  [QuoteBuilderStep.MOBILE]: TrackingEvent.QUOTE_PAGE_CODE,
};

/** Get the step name from the a URL pathname. */
export const stepName = (pathname: string): QuoteBuilderStep => {
  if (pathname.includes(`/${QuoteBuilderStep.MIX}`)) return QuoteBuilderStep.MIX;
  if (pathname.includes(`/${QuoteBuilderStep.REGISTER}`)) return QuoteBuilderStep.REGISTER;
  if (pathname.includes(`/${QuoteBuilderStep.MOBILE}`)) return QuoteBuilderStep.MOBILE;
  return QuoteBuilderStep.DETAILS;
};

export function useStepTracker(newStep: QuoteBuilderStep, prevStep: QuoteBuilderStep) {
  const { values } = useFormikContext<QuoteBuilderValues>();

  const page = trackingPageNames[prevStep];
  const toPage = trackingPageNames[newStep];

  useEffect(() => {
    setPageStart();
    setPageName(trackingPageEventNames[newStep]);
    setPageRendered();
  }, [newStep]);

  useEffect(() => {
    if (page && toPage) setCurrentPageLeaver(page, toPage);
  }, [page, toPage]);

  // Check with Zengbo if this is supposed to fire upon every value change.
  useDeepCompareEffect(() => {
    const keys = quoteStepValueKeys[newStep];
    const stepValues = pick(values, keys);

    endQuoteTracePage(toPage, stepValues);
  }, [newStep, prevStep, values, toPage]);

  useEffect(() => {
    const keys = quoteStepValueKeys[newStep];
    const stepValues = pick(values, keys);

    startQuoteTracePage(toPage, stepValues);

    // We only want the inital form populated values, so only change on newstep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newStep]);

  const locality = values.location?.locality;
  const { productTypeId } = values;

  useEffect(() => {
    if (locality)
      tracking.event(TrackingEvent.NEW_QUOTE_REGION_SELECTED, {
        region_selected: locality,
      });
  }, [locality]);

  useEffect(() => {
    if (!isNil(productTypeId)) tracking.event(TrackingEvent.NEW_QUOTE_PRODUCT_SELECTED);
  }, [productTypeId]);
}

/** Create path for next / previous steps. Return the root for number steps (i.e. from an unasigned enum). */
const createStepPath = (step: string | number) => {
  if (typeof step === 'number') return PublicRoutePath.QUOTE_PROCESS;
  return `${PublicRoutePath.QUOTE_PROCESS}/${step}`;
};

export function useCurrentUrlStep() {
  const { pathname } = useRouter();

  const urlStep = useMemo(() => stepName(pathname), [pathname]);
  const urlStepIndex = useMemo(() => stepOrder.findIndex((step) => step === urlStep), [urlStep]);

  return { urlStep, urlStepIndex };
}

/** Return info on current and previous steps; based on the current route. */
export function usePreviousFormSteps() {
  const { urlStepIndex } = useCurrentUrlStep();

  // General pricing step
  const prevIndex = useMemo(() => urlStepIndex - 1, [urlStepIndex]);

  const prevStep = useMemo(() => stepOrder[prevIndex], [prevIndex]);

  const prevStepLink = !isNil(prevStep) ? createStepPath(prevStep) : undefined;

  return { prevStep, urlStepIndex, prevStepLink };
}

export function useNextFormStep() {
  const { urlStepIndex } = useCurrentUrlStep();

  const nextStep = useMemo(() => stepOrder[urlStepIndex + 1], [urlStepIndex]);

  const nextStepLink = !isNil(nextStep) ? createStepPath(nextStep) : undefined;

  return nextStepLink;
}

/**
 * Get the correct schema for the current step
 * @param step - The current rendered quote builder step.
 */
export function useValidationStepSchema(step: QuoteBuilderStep) {
  const optionGroupsByProductTypes = useSelector(currentCartOptionGroupsByProductTypes$);

  switch (step) {
    case QuoteBuilderStep.DETAILS:
      return yup.lazy((values: DetailsStepContext) => {
        const schema = getSchema(getOptions(values.productTypeId, OptionGroupName.VOLUME, optionGroupsByProductTypes));
        return mergeSchemas(serviceAreaSchema, schema);
      });
    case QuoteBuilderStep.MIX:
      return yup.lazy((values: MixStepContext) => {
        const productType = yup.object().shape({
          [MixFormField.PRODUCT_TYPE_ID]: yup.number().defined('Please select a type of concrete').nullable(),
        });
        const schema = getSchema(getOptions(values.productTypeId, OptionGroupName.MIX, optionGroupsByProductTypes));
        return mergeSchemas(productType, serviceAreaSchema, schema);
      });
    case QuoteBuilderStep.MOBILE:
      return quoteVerificationSchema;
    case QuoteBuilderStep.REGISTER:
    default:
      return quoteSignUpSchema2;
  }
}

function trackSubmittedValues(urlStep: QuoteBuilderStep, values: QuoteBuilderValues) {
  if (urlStep === QuoteBuilderStep.DETAILS && values.location?.locality) {
    tracking.event(TrackingEvent.NEW_QUOTE_REGION_SELECTED, {
      region_selected: values.location.locality,
    });
  }
  if (urlStep === QuoteBuilderStep.MIX && !isNil(values.productTypeId)) {
    tracking.event(TrackingEvent.NEW_QUOTE_PRODUCT_SELECTED);
  }
}

/** Handle jumping to the next and previous steps */
export function useProceedHandler() {
  const { push } = useRouter();

  const { urlStep, urlStepIndex } = useCurrentUrlStep();
  const nextStepLink = useNextFormStep();

  const handleNextStep = (
    values: QuoteBuilderValues,
    actions: FormikHelpers<QuoteBuilderValues>,
    newQuoteId?: string
  ) => {
    // Reset touched, submitting and errors before navigating to the next step. Might only need submitCount and isSubmitting here?
    actions.setFormikState((prevState) => ({
      ...prevState,
      errors: {},
      touched: {},
      submitCount: 0,
      isSubmitting: false,
    }));

    trackSubmittedValues(urlStep, values);

    if (newQuoteId) {
      const newQuotePath = queryString.stringifyUrl({ url: PrivateRoutePath.ORDERS, query: { quote: newQuoteId } });
      void push(newQuotePath);
    } else if (nextStepLink) {
      const nextStepIndex = urlStepIndex + 1;
      actions.setFormikState((prevState) => ({
        ...prevState,
        status: nextStepIndex > prevState.status ? nextStepIndex : prevState.status,
      }));
      void push(nextStepLink);
    }
  };

  return handleNextStep;
}
